import React, { useState } from 'react';
import { UserIcon, CalendarIcon, DollarSignIcon, BookIcon, CameraIcon, PencilIcon, UsersIcon, SendIcon } from 'lucide-react';
interface CollaborationForm {
  projectTitle: string;
  projectType: string;
  talentNeeded: string[];
  genre: string;
  description: string;
  timeline: string;
  budget: string;
  references: string;
}
const Collaborate = () => {
  const [formData, setFormData] = useState<CollaborationForm>({
    projectTitle: '',
    projectType: '',
    talentNeeded: [],
    genre: '',
    description: '',
    timeline: '',
    budget: '',
    references: ''
  });
  const projectTypes = ['Book Cover', 'Author Portrait', 'Marketing Materials', 'Series Design', 'Other'];
  const genres = ['Romance', 'Fantasy', 'Sci-Fi', 'Mystery', 'Thriller', 'Literary Fiction', 'Non-Fiction'];
  const talentOptions = ['Photographer', 'Model', 'Designer'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleTalentToggle = (talent: string) => {
    setFormData(prev => ({
      ...prev,
      talentNeeded: prev.talentNeeded.includes(talent) ? prev.talentNeeded.filter(t => t !== talent) : [...prev.talentNeeded, talent]
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log(formData);
  };
  return <div className="min-h-screen bg-gray-50 py-8" data-id="element-15072" data-mp="2y7c5jfk0wjfm">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-15073" data-mp="2y60zf933uzlc">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-15074" data-mp="2u5qve1yvij94">
          <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-15075" data-mp="384qlle21b67e">
            Start a Collaboration
          </h1>
          <p className="text-lg text-gray-600" data-id="element-15076" data-mp="336fal0006pnl">
            Connect with talented professionals to bring your book project to
            life
          </p>
        </div>
        {/* Collaboration Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6" data-id="element-15077" data-mp="24tb4bqjrbbqj">
          {/* Project Basics */}
          <div className="space-y-4" data-id="element-15078" data-mp="29rcm885g550f">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center" data-id="element-15079" data-mp="2ek5qyw0x0dci">
              <BookIcon className="h-5 w-5 text-purple-600 mr-2" data-id="element-15080" data-mp="2jj8vmfczghjy" />
              Project Details
            </h2>
            <div data-id="element-15081" data-mp="2ohk3wlnmqk02">
              <label htmlFor="projectTitle" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15082" data-mp="2oh0kt1m8tpkg">
                Project Title
              </label>
              <input type="text" id="projectTitle" name="projectTitle" value={formData.projectTitle} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent" required data-id="element-15083" data-mp="2xhgpufimx4rf" />
            </div>
            <div data-id="element-15084" data-mp="qtpv7rss5n3s">
              <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15085" data-mp="qt6icphzlowm">
                Project Type
              </label>
              <select id="projectType" name="projectType" value={formData.projectType} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent" required data-id="element-15086" data-mp="gy4tlurmiouk">
                <option value="" data-id="element-15087" data-mp="10j9cit0uh8wj">Select a project type</option>
                {projectTypes.map(type => <option key={type} value={type} data-id="element-15088" data-mp="3pcwqvhusc7it">
                    {type}
                  </option>)}
              </select>
            </div>
            <div data-id="element-15089" data-mp="1d23n9vfa4bv5">
              <label htmlFor="genre" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15090" data-mp="1d1k2m6y8v9df">
                Genre
              </label>
              <select id="genre" name="genre" value={formData.genre} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent" required data-id="element-15091" data-mp="13460lcd2in4n">
                <option value="" data-id="element-15092" data-mp="20qwddb97zphu">Select a genre</option>
                {genres.map(genre => <option key={genre} value={genre} data-id="element-15093" data-mp="1mr3mnfxp199v">
                    {genre}
                  </option>)}
              </select>
            </div>
          </div>
          {/* Talent Needed */}
          <div className="space-y-4" data-id="element-15094" data-mp="bekr4agjjo7y">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center" data-id="element-15095" data-mp="fel4drrwltdf">
              <UsersIcon className="h-5 w-5 text-purple-600 mr-2" data-id="element-15096" data-mp="7uk8dd7s37a8" />
              Talent Needed
            </h2>
            <div className="flex flex-wrap gap-3" data-id="element-15097" data-mp="2vpqqeljrorp">
              {talentOptions.map(talent => <button key={talent} type="button" onClick={() => handleTalentToggle(talent)} className={`flex items-center px-4 py-2 rounded-full border-2 ${formData.talentNeeded.includes(talent) ? 'border-purple-600 bg-purple-50 text-purple-600' : 'border-gray-300 text-gray-700 hover:border-purple-600 hover:text-purple-600'}`} data-id="element-15098" data-mp="ht9u5h2spbvr">
                  {talent === 'Photographer' && <CameraIcon className="h-4 w-4 mr-2" data-id="element-15099" data-mp="3v1373edzmy4n" />}
                  {talent === 'Model' && <UserIcon className="h-4 w-4 mr-2" data-id="element-15100" data-mp="3q6p4aqwzxopy" />}
                  {talent === 'Designer' && <PencilIcon className="h-4 w-4 mr-2" data-id="element-15101" data-mp="2xjw2043ykhdu" />}
                  {talent}
                </button>)}
            </div>
          </div>
          {/* Project Details */}
          <div className="space-y-4" data-id="element-15102" data-mp="3r9dbfjc0755m">
            <div data-id="element-15103" data-mp="3r8ail5v4esn2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15104" data-mp="3r7sghsls1k78">
                Project Description
              </label>
              <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent" required data-id="element-15105" data-mp="4w4yvgwwynnm" />
            </div>
            <div data-id="element-15106" data-mp="3i6u9d1g1kwkf">
              <label htmlFor="references" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15107" data-mp="sjlh1qqgt20u">
                Reference Links/Examples
              </label>
              <textarea id="references" name="references" value={formData.references} onChange={handleInputChange} rows={2} placeholder="Add links to similar work you like..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-15108" data-mp="2i0h17kz1dfs1" />
            </div>
          </div>
          {/* Timeline and Budget */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-15109" data-mp="1ubfm5evp76ds">
            <div data-id="element-15110" data-mp="1ud8wb45is8qj">
              <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15111" data-mp="1pc32t441chfy">
                <CalendarIcon className="h-4 w-4 inline mr-1" data-id="element-15112" data-mp="1ke9xseegxth9" />
                Timeline
              </label>
              <select id="timeline" name="timeline" value={formData.timeline} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent" required data-id="element-15113" data-mp="3jpkglzen1zxs">
                <option value="" data-id="element-15114" data-mp="2etleca77lj7t">Select timeline</option>
                <option value="urgent" data-id="element-15115" data-mp="2et3bguq1k6qx">Urgent (1-2 weeks)</option>
                <option value="standard" data-id="element-15116" data-mp="2iu5frpytf8zr">Standard (2-4 weeks)</option>
                <option value="flexible" data-id="element-15117" data-mp="24vff60cser6n">Flexible (1-2 months)</option>
                <option value="longterm" data-id="element-15118" data-mp="24x8mziz4zhg8">Long-term (3+ months)</option>
              </select>
            </div>
            <div data-id="element-15119" data-mp="29rw3ovnwf7k9">
              <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15120" data-mp="1vuqz703u2gr4">
                <DollarSignIcon className="h-4 w-4 inline mr-1" data-id="element-15121" data-mp="1zwugw6h3ihku" />
                Budget Range
              </label>
              <select id="budget" name="budget" value={formData.budget} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent" required data-id="element-15122" data-mp="3q9a1b1kgqi8m">
                <option value="" data-id="element-15123" data-mp="1sd3c1itr48ox">Select budget range</option>
                <option value="small" data-id="element-15124" data-mp="1semtvdv22rst">$100-500</option>
                <option value="medium" data-id="element-15125" data-mp="1nksbqev3mkvu">$500-1000</option>
                <option value="large" data-id="element-15126" data-mp="1nk0i39l2ax87">$1000-2000</option>
                <option value="custom" data-id="element-15127" data-mp="229en65hlushs">Custom (to discuss)</option>
              </select>
            </div>
          </div>
          {/* Submit Button */}
          <div className="flex justify-end" data-id="element-15128" data-mp="2bb5u1dnvt5d8">
            <button type="submit" className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-15129" data-mp="2ba4kxeqfesy5">
              <SendIcon className="h-5 w-5 mr-2" data-id="element-15130" data-mp="c04pwk3b3jn3" />
              Submit Project
            </button>
          </div>
        </form>
      </div>
    </div>;
};
export default Collaborate;